import { useEffect } from "react";
import { get, Path, PathValue, useFormContext } from "react-hook-form";

import { ShopName } from "@/api/hooks/storeService.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { StoreService } from "@/services/store.service";

import { FormValuesExploded } from "../formField.types";
import { BaseOption, SelectField } from "../SelectField/selectField.component";
import { StoreFormValues } from "./storeField.schema";

type StoreFieldProps<T extends StoreFormValues> = FormValuesExploded<T> & {
    disabled?: boolean;
    placeholder?: string;
    storeList: Array<ShopName>;
    excludeStores?: Array<string>;
};

const isValidStore = (store: ShopName) => {
    return store?.code && store.shopName;
};

const StoreField = <T extends StoreFormValues>({
    name,
    label,
    required,
    microCopy,
    placeholder,
    disabled,
    storeList,
    excludeStores = []
}: StoreFieldProps<T>) => {
    const { t } = useGranite();

    const { subscribe, setValue } = useFormContext<StoreFormValues>();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const currentValue = get(values, name);
                if (currentValue && storeList && storeList.length > 0) {
                    const store = storeList.find(
                        (store) => store.code === currentValue
                    );
                    if (!store) {
                        setValue(
                            name,
                            "" as PathValue<
                                StoreFormValues,
                                Path<StoreFormValues>
                            >
                        );
                        setValue("storeIdWasInvalid", true);
                        StoreService.cleanUpPreferredStores();
                    }
                }
            }
        });

        return () => callback();
    }, [subscribe]);

    const optionList: Array<BaseOption<PathValue<T, Path<T>>>> = storeList
        .filter(isValidStore)
        .filter((store) => !excludeStores.includes(store.code))
        .map((store) => ({
            label: store.shopName,
            value: store.code as PathValue<T, Path<T>>
        }));

    return (
        <SelectField<T, BaseOption<PathValue<T, Path<T>>>>
            name={name}
            disabled={disabled}
            label={label}
            required={required}
            microCopy={microCopy}
            placeholder={placeholder}
            optionList={[
                {
                    label: t("general.form.store.default.entry"),
                    value: "" as PathValue<T, Path<T>>
                },
                ...optionList
            ]}
        />
    );
};

export { StoreField };
